/**
 * CSV Export/Import utilities for products
 */

/**
 * Convert products array to CSV string
 */
export const exportProductsToCSV = (products) => {
  if (!products || products.length === 0) {
    throw new Error('No products to export');
  }

  // Define CSV headers
  const headers = [
    'ID',
    'Name',
    'SKU',
    'Description',
    'Short Description',
    'Price',
    'Compare At Price',
    'Category',
    'Status',
    'Stock',
    'Low Stock Threshold',
    'Is Limited Edition',
    'Edition Size',
    'Artist',
    'Dimensions',
    'Materials',
    'Is Featured',
    'Has Audio Description',
    'Has Video Content',
    'Video URL',
    'Video Type',
    'Audio URL',
    'Features',
    'Tags',
    'Created At',
    'Updated At',
  ];

  // Convert products to CSV rows
  const rows = products.map(product => [
    product.id || '',
    escapeCSVValue(product.name || ''),
    product.sku || '',
    escapeCSVValue(product.description || ''),
    escapeCSVValue(product.shortDescription || ''),
    product.price || '',
    product.compareAtPrice || '',
    product.category?.name || '',
    product.status || '',
    product.stock ?? '',
    product.lowStockThreshold ?? '',
    product.isLimitedEdition ? 'Yes' : 'No',
    product.editionSize || '',
    escapeCSVValue(product.artist || ''),
    escapeCSVValue(product.dimensions || ''),
    escapeCSVValue(product.materials || ''),
    product.isFeatured ? 'Yes' : 'No',
    product.hasAudioDescription ? 'Yes' : 'No',
    product.hasVideoContent ? 'Yes' : 'No',
    product.videoUrl || '',
    product.videoType || '',
    product.audioUrl || '',
    escapeCSVValue(Array.isArray(product.features) ? product.features.join('|') : ''),
    escapeCSVValue(product.tags || ''),
    product.createdAt || '',
    product.updatedAt || '',
  ]);

  // Combine headers and rows
  const csv = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csv;
};

/**
 * Escape CSV values that contain commas, quotes, or newlines
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // If value contains comma, quote, or newline, wrap in quotes and escape existing quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent, filename = 'products.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse CSV string to array of objects
 */
export const parseCSVToProducts = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('CSV file is empty or invalid');
  }

  // Parse headers
  const headers = parseCSVLine(lines[0]);

  // Parse rows
  const products = [];
  const errors = [];

  for (let i = 1; i < lines.length; i++) {
    try {
      const values = parseCSVLine(lines[i]);

      if (values.length !== headers.length) {
        errors.push({ line: i + 1, error: 'Column count mismatch' });
        continue;
      }

      const product = {};
      headers.forEach((header, index) => {
        product[header] = values[index];
      });

      // Convert and validate product data
      const convertedProduct = convertCSVRowToProduct(product, i + 1);
      products.push(convertedProduct);
    } catch (err) {
      errors.push({ line: i + 1, error: err.message });
    }
  }

  return { products, errors };
};

/**
 * Parse a single CSV line, handling quoted values
 */
const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of value
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  // Add last value
  values.push(current.trim());

  return values;
};

/**
 * Convert CSV row object to product object with proper types
 */
const convertCSVRowToProduct = (row, lineNumber) => {
  try {
    return {
      id: row.ID || undefined,
      name: row.Name || '',
      sku: row.SKU || '',
      description: row.Description || '',
      shortDescription: row['Short Description'] || '',
      price: parseFloat(row.Price) || 0,
      compareAtPrice: row['Compare At Price'] ? parseFloat(row['Compare At Price']) : null,
      category: row.Category || '',
      status: row.Status || 'DRAFT',
      stock: parseInt(row.Stock) || 0,
      lowStockThreshold: parseInt(row['Low Stock Threshold']) || 5,
      isLimitedEdition: row['Is Limited Edition']?.toLowerCase() === 'yes',
      editionSize: row['Edition Size'] ? parseInt(row['Edition Size']) : null,
      artist: row.Artist || '',
      dimensions: row.Dimensions || '',
      materials: row.Materials || '',
      isFeatured: row['Is Featured']?.toLowerCase() === 'yes',
      hasAudioDescription: row['Has Audio Description']?.toLowerCase() === 'yes',
      hasVideoContent: row['Has Video Content']?.toLowerCase() === 'yes',
      videoUrl: row['Video URL'] || '',
      videoType: row['Video Type'] || '',
      audioUrl: row['Audio URL'] || '',
      features: row.Features ? row.Features.split('|').filter(f => f.trim()) : [],
      tags: row.Tags || '',
    };
  } catch (err) {
    throw new Error(`Line ${lineNumber}: ${err.message}`);
  }
};

/**
 * Validate imported products
 */
export const validateImportedProducts = (products) => {
  const validationErrors = [];

  products.forEach((product, index) => {
    const errors = [];

    // Required fields
    if (!product.name || product.name.trim() === '') {
      errors.push('Name is required');
    }
    if (!product.sku || product.sku.trim() === '') {
      errors.push('SKU is required');
    }
    if (!product.description || product.description.trim() === '') {
      errors.push('Description is required');
    }

    // Price validation
    if (isNaN(product.price) || product.price <= 0) {
      errors.push('Price must be a positive number');
    }

    // Stock validation
    if (isNaN(product.stock) || product.stock < 0) {
      errors.push('Stock must be a non-negative number');
    }

    // Status validation
    const validStatuses = ['DRAFT', 'ACTIVE', 'ARCHIVED', 'OUT_OF_STOCK'];
    if (!validStatuses.includes(product.status)) {
      errors.push(`Status must be one of: ${validStatuses.join(', ')}`);
    }

    if (errors.length > 0) {
      validationErrors.push({
        row: index + 2, // +2 because: +1 for header, +1 for 0-based index
        sku: product.sku,
        errors,
      });
    }
  });

  return validationErrors;
};
